import type { YieldFetcher } from '../../yields/types'
import { wstethFetcher } from '../../yields/intrinsic/fetchers/lido'
import { rethFetcher } from '../../yields/intrinsic/fetchers/rocketpool'
import { ezethFetcher } from '../../yields/intrinsic/fetchers/renzo'
import { rsethFetcher } from '../../yields/intrinsic/fetchers/kelp'
import { rswethFetcher } from '../../yields/intrinsic/fetchers/swell'
import { swBtcFetcher } from '../../yields/intrinsic/fetchers/swBTC'
import { pufethFetcher } from '../../yields/intrinsic/fetchers/puffer'
import { osethFetcher } from '../../yields/intrinsic/fetchers/stakewise'
import {
  ethxFetcher,
  maticxFetcher,
} from '../../yields/intrinsic/fetchers/stader'
import {
  trumaticFetcher,
  trupolFetcher,
} from '../../yields/intrinsic/fetchers/trufin'
import { kinetiqKHypeFetcher } from '../../yields/intrinsic/fetchers/kinetiq'
import {
  hbhypeFetcher,
  hbuhypeFetcher,
} from '../../yields/intrinsic/fetchers/hyperbeat'
import { cbethFetcher } from '../../yields/intrinsic/fetchers/coinbase'
import { methFetcher } from '../../yields/intrinsic/fetchers/mantle'
import { yieldNestFetcher } from '../../yields/intrinsic/fetchers/yieldnest'
import { stsFetcher } from '../../yields/intrinsic/fetchers/stS'
import { savaxFetcher } from '../../yields/intrinsic/fetchers/savax'
import { stceloFetcher } from '../../yields/intrinsic/fetchers/stcelo'
import { valantisStHypeFetcher } from '../../yields/intrinsic/fetchers/valantis'
import { stKaiaFetcher } from '../../yields/intrinsic/fetchers/lair'
import { slisBnbFetcher } from '../../yields/intrinsic/fetchers/lista'
import {
  iberaFetcher,
  ibgtFetcher,
} from '../../yields/intrinsic/fetchers/infrared'
import { beraEthFetcher } from '../../yields/intrinsic/fetchers/dinero'
import { ankrFetcher } from '../../yields/intrinsic/fetchers/ankr'
import { lbgtFetcher } from '../../yields/intrinsic/fetchers/berapaw'
import { psxdcFetcher } from '../../yields/intrinsic/fetchers/primestaking'
import {
  lombardLbtcFetcher,
  lombardLbtcvFetcher,
} from '../../yields/intrinsic/fetchers/lombard'
import { ebtcFetcher } from '../../yields/intrinsic/fetchers/etherfi'
import { solvBtcPlusFetcher } from '../../yields/intrinsic/fetchers/solv'
import { coreLstFetcher } from '../../yields/intrinsic/fetchers/core'
import { monadLstFetcher } from '../../yields/intrinsic/fetchers/monadLst'
import { sfrxEthFetcher } from '../../yields/intrinsic/fetchers/frax'
import { wbethFetcher } from '../../yields/intrinsic/fetchers/binance'
import {
  tavaxFetcher,
  tethFetcher,
} from '../../yields/intrinsic/fetchers/treehouse'
import { stxrpFetcher } from '../../yields/intrinsic/fetchers/firelight'
import type { LstWithdrawalMode } from './types'

/** Reader kind — picks which on-chain call set the orchestrator emits
 *  for a given LST. See `readers.ts` for the per-kind implementations. */
export type LstReaderKind =
  | 'erc4626'
  | 'erc4626PreviewRedeem'
  | 'lidoWstEth'
  | 'flarePooledFlr'
  | 'rocketReth'
  | 'etherfiWeEth'
  | 'renzoEzEth'
  | 'kelpRsEth'
  | 'swellGetRate'
  | 'binanceWbeth'
  | 'stakewiseOsEth'
  | 'staderEthx'
  | 'staderMaticX'
  | 'mantleMEth'
  | 'beetsStS'
  | 'benqiSavax'
  | 'kinetiqKHype'
  | 'valantisWstHype'
  | 'hyperbeatBeHype'
  | 'stceloManager'
  | 'lairStKaia'
  | 'listaSlisBnb'
  | 'dineroBeraEth'
  | 'bgtWrapper1to1'
  | 'ankrRatio'
  | 'coreEarnRate'
  | 'coreStakedRatio'
  | 'vedaAccountant'
  | 'kintsuSMon'
  | 'treehouseTAsset'
  | 'offChain'

/**
 * Static registry entry for an LST share-token.
 *
 * Per-chain LSTs live in {@link LST_REGISTRY}. Each entry pins the
 * on-chain identity (address, decimals, brand), the reader strategy
 * (which contracts to multicall and how to compute the rate), the
 * mint/withdrawal metadata used by downstream calldata routing, and a
 * pointer to the existing intrinsic-yield fetcher whose APR is
 * surfaced as `supplyRate`.
 */
export interface LstRegistryEntry {
  /** Share-token contract address, lowercased. */
  address: string
  /** Underlying asset address, lowercased — `zeroAddress` for native
   *  ETH, an ERC-20 address otherwise (e.g. wstETH for pufETH). This is
   *  the **pay asset** (deposit input / what the share token wraps), so it
   *  drives deposit routing — not just pricing. */
  underlying: string
  /** Price-only reference asset, lowercased. When the `underlying` (pay
   *  asset) has no USD price of its own, this points pricing at an
   *  economically-equivalent, *priced* token — used **solely** for
   *  `priceUsd` (and the USD fields derived from it), never for deposits.
   *  e.g. the BGT wrappers pay in BGT (unpriced) but are 1:1 redeemable to
   *  BERA, so `priceUnderlying` = WBERA. Omit when `underlying` is priced. */
  priceUnderlying?: string
  /** Share-token symbol. */
  symbol: string
  /** Display name — `${brand} ${symbol}` is composed in the parser if
   *  this is omitted. */
  brand: string
  /** Share decimals. */
  decimals: number
  /** Underlying asset decimals. Defaults to `decimals` when omitted — set
   *  it only when the share token and its underlying differ, e.g. 18-dec
   *  SolvBTC over 8-dec WBTC, or 8-dec bridged pumpBTC over 18-dec
   *  SolvBTC.CORE. Load-bearing for share→asset conversion
   *  (`convertToAssets`/`convertToShares`, `totalAssets` scaling, and the
   *  cross-provider `sharePrice`). Mirrors `SavingsRegistryEntry.underlyingDecimals`. */
  underlyingDecimals?: number
  /** Reader strategy — drives the multicall payload. */
  reader: LstReaderKind
  /** Whether this is a real ERC-4626 (informational; the reader still
   *  decides the call shape). */
  isErc4626: boolean
  /** Whether the share token rebases. */
  isRebasing: boolean
  /** Whether the LST can be minted permissionlessly on-chain. */
  isMintable: boolean
  /** Whether the mint entry accepts native ETH. */
  isNativeUnderlying: boolean
  /** Mint entry contract — used by calldata-sdk when wiring deposits. */
  mintContract?: string
  /** Mint input asset. */
  mintInputAsset?: 'native' | string
  /** Withdrawal mechanism. */
  withdrawalMode: LstWithdrawalMode
  /** Fixed cooldown in seconds (only for `fixed-cooldown` mode). */
  withdrawalCooldownSeconds?: number
  /** Existing intrinsic-yield fetcher whose APR is surfaced as
   *  `supplyRate`. Undefined when no yield source exists yet (e.g.
   *  ynLSDe). */
  yieldFetcher?: YieldFetcher
  /** Key inside the yield fetcher's `YieldResult` to read. Yield
   *  fetchers return maps keyed by symbol or by a free-text label;
   *  this is the exact key. */
  yieldKey?: string
  /** Extra contracts the reader needs (oracle, deposit pool, …).
   *  Shape is reader-specific. */
  readerExtras?: {
    /** Renzo `RestakeManager` (chainId 1: 0x74a09653a083691711cf8215a6ab074bb4e99ef5). */
    restakeManager?: string
    /** Kelp `LRTOracle` (chainId 1: 0x349a73444b1a310bae67ef67973022020d70020d). */
    lrtOracle?: string
    /** StakeWise `OsTokenVaultController` (chainId 1:
     *  0x2a261e60fb14586b474c208b1b7ac6d0f5000306). */
    osTokenController?: string
    /** Stader `StaderStakePoolsManager` (chainId 1:
     *  0xcf5ea1b38380f6af39068375516daf40ed70d299). */
    staderStakePoolsManager?: string
    /** Mantle `Staking` (chainId 1: 0xe3cbd06d7dadb3f4e6557bab7edd924cd1489e8f). */
    mantleStaking?: string
    /** Rocket Pool `RocketDepositPool` (current address — resolve via
     *  `RocketStorage` at deploy time; this is a snapshot). */
    rocketDepositPool?: string
    /** EtherFi `LiquidityPool` — exposes the pooled-ETH buffer. */
    etherFiLiquidityPool?: string
    /** Kinetiq `StakingAccountant` (chainId 999:
     *  0x9209648Ec9D448EF57116B73A2f081835643dc7A) — exposes
     *  `kHYPEToHYPE(uint256)`. */
    kinetiqAccountant?: string
    /** Valantis `stHYPE` (chainId 999:
     *  0xffaa4a3d97fe9107cef8a3f48c069f577ff76cc1) — exposes
     *  `balancePerShare()` (1e18-scaled HYPE-per-share). */
    valantisStHype?: string
    /** Hyperbeat `StakingCore` (chainId 999:
     *  0xcEAD893b162D38e714D82d06a7fe0b0dc3c38E0b) — exposes
     *  `BeHYPEToHYPE(uint256)`. */
    hyperbeatStakingCore?: string
    /** StakedCelo `Manager` (Celo mainnet 42220:
     *  0x0239b96D10a434a56CC9E09383077A0490cF9398) — exposes
     *  `toCelo(uint256)` (1e18-scaled CELO-per-stCELO). */
    stceloManager?: string
    /** Stader MaticX **home** contract address, used by the
     *  cross-chain Polygon entry. The Polygon bridge child has no
     *  rate getter — the rate is read from the Ethereum home
     *  contract (`0xf03a…b645`) via cross-chain dispatch. */
    staderMaticXHomeContract?: string
    /** Chain to dispatch the rate-getter call to. Pairs with
     *  `staderMaticXHomeContract`. */
    staderMaticXHomeChainId?: string
    /** Lista `ListaStakeManager` (BNB chain 56:
     *  0x1adb950d8bb3da4be104211d5ab038628e477fe6) — exposes
     *  `convertSnBnbToBnb(uint256)` (1e18-scaled BNB-per-slisBNB) and
     *  `getTotalPooledBnb()`. The slisBNB token itself carries no rate. */
    listaStakeManager?: string
    /** Veda BoringVault `AccountantWithRateProviders` (eBTC on chain 1:
     *  0x1b293DC39F94157fA0D1D36d7e0090C8B8B8c13F) — exposes
     *  `getRate()` (assets-per-share, base-asset decimals). The eBTC
     *  vault token carries no ERC-4626 rate surface. */
    vedaAccountant?: string
    /** stCORE `Earn` contract (Core chain 1116:
     *  0xf5fA1728bABc3f8D2a617397faC2696c958C3409 = `stCORE.earn()`) —
     *  exposes `getCurrentExchangeRate()` (1e6-scaled CORE-per-stCORE).
     *  The stCORE token itself carries no rate getter. */
    coreEarn?: string
    /** Treehouse `TreehouseFastlane` — the instant-exit module, whose
     *  `getRedeemableAmount()` is the row's `liquidity`. Per chain:
     *  Ethereum `0x829525417cd78cba0f99a8736426fc299506c0d6`,
     *  Avalanche `0x3d00a639183b07e35efef044ee6cc14e8598a01c`,
     *  HyperEVM `0x3018e0747549835faa8d06cf4809b1a2519fab31`.
     *  Discovered from `Vault.redemption()` →
     *  `RedemptionController.getRedemptionContracts()`; Treehouse
     *  documents none of them. */
    treehouseFastlane?: string
    /** Treehouse `TreehouseRedemptionV2` — the queued-exit module, read
     *  for its live `waitingPeriod()`. Per chain: Ethereum
     *  `0xcd63a29faff07130d3af89bb4f40778938aabb85`, Avalanche
     *  `0x765f6dc8496ca7ef1e4a391be10185229aacf04b`, HyperEVM
     *  `0xcebbb011c2f29b0ed25895c54662bd1ccad30280`. */
    treehouseRedemption?: string
    /** Core staking contract holding `totalStaked()` when it is not the
     *  share token itself — dualCORE's b14g `coreVault`
     *  (0xee21ab613d30330823D35Cf91A84cE964808B83F = `dualCORE.coreVault()`).
     *  SCORE omits this (it is its own staking contract). */
    coreStakeContract?: string
  }
}

/**
 * Per-chain LST allowlist. Keyed by chainId (string to match the rest
 * of the codebase). Add new chains by appending an entry; the LST
 * vault provider returns `{}` for chains with no entry.
 *
 * Coverage: Ethereum mainnet only in this drop — see VAULT_CANDIDATES.md
 * for the broader roadmap (HyperEVM LSTs, ezETH on L2s, etc.).
 */
export const LST_REGISTRY: { [chainId: string]: LstRegistryEntry[] } = {
  // Monad (143) — native-MON LSTs. shMON / aprMON are ERC-4626 over native
  // MON (asset() = the 0xEeee… native sentinel), with async redemptions
  // (requestRedeem → claim). Priced via WMON. sMON (Kintsu) is a custom
  // non-4626 contract and is added separately once its rate-getter ABI is
  // available.
  '143': [
    {
      address: '0x1b68626dca36c7fe922fd2d55e4f631d962de19c',
      underlying: '0x0000000000000000000000000000000000000000',
      priceUnderlying: '0x3bd359c1119da7da1d913d1c4d2b7c461115433a', // WMON
      symbol: 'shMON',
      brand: 'ShMonad',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x1b68626dca36c7fe922fd2d55e4f631d962de19c',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: monadLstFetcher,
      yieldKey: 'ShMonad::shMON',
    },
    {
      address: '0x0c65a0bc65a5d819235b71f554d210d3f80e0852',
      underlying: '0x0000000000000000000000000000000000000000',
      priceUnderlying: '0x3bd359c1119da7da1d913d1c4d2b7c461115433a', // WMON
      symbol: 'aprMON',
      brand: 'aPriori',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x0c65a0bc65a5d819235b71f554d210d3f80e0852',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      // No DefiLlama pool for aPriori — `supplyRate` is left to the on-chain
      // exchange-rate path; the ERC-4626 `convertToAssets` rate is surfaced.
    },
    {
      // sMON (Kintsu) — custom (non-4626) native-MON LST. Rate derived from
      // totalPooled()/totalShares(). Deposit/withdraw mechanics are custom
      // and not yet wired on the action side (no verified source ABI).
      address: '0xa3227c5969757783154c60bf0bc1944180ed81b9',
      underlying: '0x0000000000000000000000000000000000000000',
      priceUnderlying: '0x3bd359c1119da7da1d913d1c4d2b7c461115433a', // WMON
      symbol: 'sMON',
      brand: 'Kintsu',
      decimals: 18,
      reader: 'kintsuSMon',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      withdrawalMode: 'queued',
      yieldFetcher: monadLstFetcher,
      yieldKey: 'Kintsu Staked Monad::sMON',
    },
    {
      // gMON (Magma) — ERC-4626 over WMON (ERC-20 underlying, priced
      // directly). Deposit pulls WMON; redemption is sync/async on-chain.
      // Action side not yet wired (deposit reverts opaquely without source).
      address: '0x8498312a6b3cbd158bf0c93abdcf29e6e4f55081',
      underlying: '0x3bd359c1119da7da1d913d1c4d2b7c461115433a', // WMON
      symbol: 'gMON',
      brand: 'Magma',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x8498312a6b3cbd158bf0c93abdcf29e6e4f55081',
      withdrawalMode: 'queued',
      yieldFetcher: monadLstFetcher,
      yieldKey: 'gMON::gMON',
    },
  ],
  '1': [
    {
      // Treehouse tETH — an ERC-4626 share over wstETH that runs a
      // leveraged staking carry (wstETH collateral on Aave Core / Aave
      // Prime / Spark, ETH borrowed against it, converted back to wstETH).
      //
      // THREE things the generic 4626 path gets wrong here:
      //  1. `asset()` is NOT wstETH — it returns
      //     `InternalAccountingUnit_Wrapped liquid staked Ether 2.0`
      //     (`IAU_wstETH`, 0x1b6238e9…), a synthetic unit minted 1:1
      //     against the real underlying. The underlying below is
      //     `Vault.getUnderlying()`; never read `asset()`.
      //  2. Entry is the `TreehouseRouter`, not the share token. It takes
      //     native ETH (`depositETH()`) or any `Vault.isAllowableAsset`
      //     ERC-20 (stETH / WETH / wstETH) via `deposit(asset, amount)`,
      //     and **credits `msg.sender`** — there is no receiver argument.
      //  3. Exit is neither of the share's own 4626 legs: `maxWithdraw`
      //     and `maxRedeem` both read 0. It is the Fastlane (instant,
      //     50 bps) or RedemptionV2 (queued, 5 bps) — hence
      //     `fee-or-queued`, with `liquidity` measured by the reader off
      //     `Fastlane.getRedeemableAmount()`.
      address: '0xd11c452fc99cf405034ee446803b6f6c1f6d5ed8',
      underlying: '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0', // wstETH
      symbol: 'tETH',
      brand: 'Treehouse',
      decimals: 18,
      reader: 'treehouseTAsset',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xefa3fa8e85d2b3cfdb250cdea156c2c6c90628f5', // TreehouseRouter
      mintInputAsset: 'native',
      withdrawalMode: 'fee-or-queued',
      // Read live by the reader (7 d today, and it is an owner dial).
      withdrawalCooldownSeconds: 7 * 86_400,
      yieldFetcher: tethFetcher,
      yieldKey: 'Treehouse ETH::TETH',
      readerExtras: {
        treehouseFastlane: '0x829525417cd78cba0f99a8736426fc299506c0d6',
        treehouseRedemption: '0xcd63a29faff07130d3af89bb4f40778938aabb85',
      },
    },
    {
      address: '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'wstETH',
      brand: 'Lido',
      decimals: 18,
      reader: 'lidoWstEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: wstethFetcher,
      yieldKey: 'WSTETH',
    },
    {
      // Frax sfrxETH — a plain ERC-4626 over **frxETH**, not over ETH:
      // `asset()` is frxETH and `convertToAssets` is denominated in it,
      // so `underlying` is frxETH rather than the native sentinel.
      //
      // Two exits, and they behave differently. sfrxETH → frxETH is an
      // unconditional same-block `redeem` (the vault holds the whole
      // frxETH float), which is what `withdrawalMode` describes. frxETH
      // → ETH has **no protocol redemption at all** — the frxETHMinter
      // is one-way — so that leg is DEX-only (Curve frxETH/ETH). Modeled
      // as `instant-or-queued` because the *listed underlying* is always
      // instantly redeemable; see LST_DEPOSIT_INTERFACES.md.
      address: '0xac3e018457b222d93114458476f3e3416abbe38f',
      underlying: '0x5e8422345238f34275888049021821e8e08caa1f', // frxETH
      symbol: 'sfrxETH',
      brand: 'Frax',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      // Native ETH mints in one call via `frxETHMinter.submitAndDeposit`
      // (ETH → frxETH → sfrxETH); the frxETH ERC-20 path is the plain
      // 4626 `deposit`. Both are declared in the calldata-sdk
      // `fraxSfrxEth` accept-set.
      isNativeUnderlying: true,
      mintContract: '0xac3e018457b222d93114458476f3e3416abbe38f',
      mintInputAsset: '0x5e8422345238f34275888049021821e8e08caa1f',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: sfrxEthFetcher,
      yieldKey: 'SFRXETH',
    },
    {
      address: '0xae78736cd615f374d3085123a210448e74fc6393',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'rETH',
      brand: 'Rocket Pool',
      decimals: 18,
      reader: 'rocketReth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xdd3f50f8a6cafbe9b31a427582963f465e745af8',
      mintInputAsset: 'native',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: rethFetcher,
      yieldKey: 'RETH',
      readerExtras: {
        rocketDepositPool: '0xdd3f50f8a6cafbe9b31a427582963f465e745af8',
      },
    },
    {
      address: '0xcd5fe23c85820f7b72d0926fc9b05b43e359b7ee',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'weETH',
      brand: 'EtherFi',
      decimals: 18,
      reader: 'etherfiWeEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x308861a430be4cce5502d0a12724771fc6daf216',
      mintInputAsset: 'native',
      withdrawalMode: 'instant-or-queued',
      // weETH yield is composed: wstETH APR + EtherFi's LRT2 bonus.
      // The orchestrator special-cases this — no yieldFetcher here.
      readerExtras: {
        etherFiLiquidityPool: '0x308861a430be4cce5502d0a12724771fc6daf216',
      },
    },
    {
      address: '0xbf5495efe5db9ce00f80364c8b423567e58d2110',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ezETH',
      brand: 'Renzo',
      decimals: 18,
      reader: 'renzoEzEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x74a09653a083691711cf8215a6ab074bb4e99ef5',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      // On-chain WithdrawQueue.coolDownPeriod() = 1_209_600s (14 days).
      withdrawalCooldownSeconds: 14 * 86400,
      yieldFetcher: ezethFetcher,
      yieldKey: 'EZETH',
      readerExtras: {
        restakeManager: '0x74a09653a083691711cf8215a6ab074bb4e99ef5',
      },
    },
    {
      address: '0xa1290d69c65a6fe4df752f95823fae25cb99e5a7',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'rsETH',
      brand: 'Kelp',
      decimals: 18,
      reader: 'kelpRsEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x036676389e48133b63a802f8635ad39e752d375d',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 8 * 86400,
      yieldFetcher: rsethFetcher,
      yieldKey: 'RSETH',
      readerExtras: {
        lrtOracle: '0x349a73444b1a310bae67ef67973022020d70020d',
      },
    },
    {
      address: '0xfae103dc9cf190ed75350761e95403b7b8afa6c0',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'rswETH',
      brand: 'Swell',
      decimals: 18,
      reader: 'swellGetRate',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xfae103dc9cf190ed75350761e95403b7b8afa6c0',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: rswethFetcher,
      yieldKey: 'Restaked Swell ETH::RSWETH',
    },
    {
      // Puffer pufETH. The underlying is **WETH**, not wstETH: `asset()`
      // returns `0xC02aaA39…` and the vault holds ZERO wstETH (verified
      // on-chain 2026-08-25). It was wstETH-denominated at launch and the
      // registry never followed the migration, so the row valued 24,108 ETH
      // of assets at the wstETH price — a ~20 % TVL overstatement, and a
      // `convertToAssets` of 1.082 labelled in the wrong unit.
      address: '0xd9a442856c234a39a81a089c06451ebaa4306a72',
      underlying: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
      symbol: 'pufETH',
      brand: 'Puffer',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xd9a442856c234a39a81a089c06451ebaa4306a72',
      // The builder's only path is the native `depositETH(receiver)` zap.
      mintInputAsset: 'native',
      withdrawalMode: 'fee-or-queued',
      yieldFetcher: pufethFetcher,
      yieldKey: 'PUFETH',
    },
    {
      address: '0x09db87a538bd693e9d08544577d5ccfaa6373a48',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ynETH',
      brand: 'YieldNest',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x09db87a538bd693e9d08544577d5ccfaa6373a48',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: yieldNestFetcher,
      yieldKey: 'YieldNest Restaked ETH::YNETH',
    },
    {
      // YieldNest ynETH MAX — ERC-4626 vault over WETH/ynETH/ynLSDe/ETH.
      // Exits via a first-come liquidity buffer (instant under normal
      // conditions; a 0%-fee withdrawal queue is in development), so no
      // NFT-enumerable request surface — withdrawal reader is `noQueue`.
      address: '0x657d9aba1dbb59e53f9f3ecaa878447dcfc96dcb',
      underlying: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
      symbol: 'ynETHx',
      brand: 'YieldNest',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x657d9aba1dbb59e53f9f3ecaa878447dcfc96dcb',
      mintInputAsset: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: yieldNestFetcher,
      yieldKey: 'ynETH MAX::YNETHX',
    },
    {
      // YieldNest ynLSDe — multi-asset LRT restaking LSTs (stETH/wstETH,
      // sfrxETH, mETH, OETH/wOETH) into EigenLayer. ETH-denominated
      // accounting (18d). `convertToAssets` reverts on this vault, so the
      // rate is read via `previewRedeem`. Withdrawals use the same
      // WithdrawalQueueManager NFT surface as ynETH (redeemableAsset ==
      // ynLSDe), at `0x8Face3…` — see withdrawals/registry.ts.
      address: '0x35ec69a77b79c255e5d47d5a3bdbefefe342630c',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ynLSDe',
      brand: 'YieldNest',
      decimals: 18,
      reader: 'erc4626PreviewRedeem',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x35ec69a77b79c255e5d47d5a3bdbefefe342630c',
      // Multi-asset deposit `deposit(IERC20 asset, uint256, address)`.
      // wstETH is the primary accepted LST; others (sfrxETH, mETH, OETH)
      // also work via the same entry.
      mintInputAsset: '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0',
      withdrawalMode: 'queued',
      yieldFetcher: yieldNestFetcher,
      yieldKey: 'YieldNest Restaked LSD Eigenlayer::YNLSDE',
    },
    {
      address: '0xf1c9acdc66974dfb6decb12aa385b9cd01190e38',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'osETH',
      brand: 'StakeWise',
      decimals: 18,
      reader: 'stakewiseOsEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xac0f906e433d58fa868f936e8a43230473652885',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 8 * 86400,
      yieldFetcher: osethFetcher,
      yieldKey: 'StakeWise Staked ETH::OSETH',
      readerExtras: {
        osTokenController: '0x2a261e60fb14586b474c208b1b7ac6d0f5000306',
      },
    },
    {
      address: '0xa35b1b31ce002fbf2058d22f30f95d405200a15b',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ETHx',
      brand: 'Stader',
      decimals: 18,
      reader: 'staderEthx',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xcf5ea1b38380f6af39068375516daf40ed70d299',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: ethxFetcher,
      yieldKey: 'Stader ETHx::ETHx',
      readerExtras: {
        staderStakePoolsManager: '0xcf5ea1b38380f6af39068375516daf40ed70d299',
      },
    },
    {
      address: '0xd5f7838f5c461feff7fe49ea5ebaf7728bb0adfa',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'mETH',
      brand: 'Mantle',
      decimals: 18,
      reader: 'mantleMEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xe3cbd06d7dadb3f4e6557bab7edd924cd1489e8f',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: methFetcher,
      yieldKey: 'METH',
      readerExtras: {
        mantleStaking: '0xe3cbd06d7dadb3f4e6557bab7edd924cd1489e8f',
      },
    },
    {
      address: '0xbe9895146f7af43049ca1c1ae358b0541ea49704',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'cbETH',
      brand: 'Coinbase',
      decimals: 18,
      // Coinbase publishes the rate off-chain (Chainlink feed, not
      // on the token itself). The `offChain` reader just produces
      // totalSupply + 1.0 placeholder rate; `supplyRate` comes from
      // the existing cbethFetcher.
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      // Coinbase mints off-chain — surface it as a vault entry for
      // discoverability but flag the route as non-mintable so calldata-sdk
      // falls back to swap.
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'off-chain',
      yieldFetcher: cbethFetcher,
      yieldKey: 'CBETH',
    },
    {
      // Binance wBETH — the *other* exchange LST, and unlike cbETH above
      // it is genuinely permissionless on-chain in both directions:
      //   mint   `deposit(address referral)` payable, no allowlist
      //   redeem `requestWithdrawEth(uint256)` → the UnwrapTokenV1ETH
      //          queue at 0x79973d557CD9dd87eb61E250cc2572c990e20196
      // (both simulated against mainnet — `deposit` succeeds from an
      // arbitrary EOA, `requestWithdrawEth` reverts only on balance).
      //
      // A FiatTokenProxy (Circle's USDC codebase) + Binance's
      // StakedTokenV3 mixin, so it inherits USDC-style `blacklist(address)`
      // and `pause()` on BOTH the token and the unwrap queue — Binance can
      // freeze any holder. Same trust class as USDC; that is the live risk
      // for anything treating wBETH as collateral.
      //
      // The `queued` exit has two teeth that a plain cooldown does not:
      //   * the ETH owed is FROZEN at request time (`ethAmount` is stored,
      //     not recomputed), so the position stops earning for the whole
      //     `lockTime()` — currently 864000s / 10 days, admin-settable down
      //     to MIN_LOCK_TIME = 172800s / 2 days. Read it live.
      //   * a request is only auto-allocated while
      //     `availableAllocateAmount` covers it (~3 ETH on Ethereum today);
      //     anything larger waits for Binance's operator to `allocate()`,
      //     with no SLA.
      address: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'wBETH',
      brand: 'Binance',
      decimals: 18,
      reader: 'binanceWbeth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: wbethFetcher,
      yieldKey: 'Wrapped Binance Beacon ETH::wBETH',
    },
    {
      address: '0xa43a7c62d56df036c187e1966c03e2799d8987ed',
      // TruFin TruStake MATIC Vault uses the MATIC ERC-20 (not POL).
      underlying: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0',
      symbol: 'TruMATIC',
      brand: 'TruFin',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0xa43a7c62d56df036c187e1966c03e2799d8987ed',
      mintInputAsset: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0',
      withdrawalMode: 'queued',
      yieldFetcher: trumaticFetcher,
      yieldKey: 'TRUMATIC',
    },
    {
      // TruFin TruStake POL Vault — the POL successor to TruMATIC
      // (MATIC is migrating to POL). Same vault family/interface as
      // TruStakeMATICv2: convertToAssets for the rate, validator-unbond
      // withdrawals. Underlying is the POL ERC-20 (not MATIC).
      address: '0xc10214cde5d6754ec1e2220362f2120142c8e5e8',
      underlying: '0x455e53cbb86018ac2b8092fdcd39d8444affc3f6',
      symbol: 'TruPOL',
      brand: 'TruFin',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0xc10214cde5d6754ec1e2220362f2120142c8e5e8',
      mintInputAsset: '0x455e53cbb86018ac2b8092fdcd39d8444affc3f6',
      withdrawalMode: 'queued',
      yieldFetcher: trupolFetcher,
      yieldKey: 'TRUPOL',
    },
    {
      // Stader MaticX home contract — rate logic on Ethereum, even
      // though most holders are on Polygon (bridged child token at
      // 0xfa68…b645). Registering the L1 contract because that's
      // where the canonical rate getter lives.
      address: '0xf03a7eb46d01d9ecaa104558c732cf82f6b6b645',
      underlying: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0',
      symbol: 'MaticX',
      brand: 'Stader',
      decimals: 18,
      reader: 'staderMaticX',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0xf03a7eb46d01d9ecaa104558c732cf82f6b6b645',
      mintInputAsset: '0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0',
      withdrawalMode: 'queued',
      yieldFetcher: maticxFetcher,
      yieldKey: 'MATICX',
    },
    {
      // Ankr ankrETH (aETHc) — native ETH stake via ethereumPool
      // `stakeAndClaimAethC()`; unstake via `unstakeAETH(shares)`
      // (queued — Ankr fulfils the payout, no user claim tx). `ratio()`
      // is shares-per-ETH, so the reader inverts it.
      address: '0xe95a203b1a91a908f9b9ce46459d101078c2c3cb',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ankrETH',
      brand: 'Ankr',
      decimals: 18,
      reader: 'ankrRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x84db6ee82b7cf3b47e8f19270abde5718b936670',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: ankrFetcher,
      yieldKey: 'ANKRETH',
    },
    // ── BTC LSTs / LRTs (8-dec, WBTC-priced) ─────────────────────────
    // Research + on-chain verification: see
    // `vaults/lst/BTC_LSTS.md`. WBTC is used as the priced underlying
    // (≈ BTC) for all of them.
    {
      // Swell swBTC — Yearn-V3 ERC-4626 vault over WBTC. Rate via
      // `convertToAssets`. ERC-4626 interface, but the real exit is a
      // ~10-day queue (Swell + Symbiotic) — modelled as `queued`.
      // Yield via on-chain share-rate delta (no public APR API).
      address: '0x8db2350d78abc13f5673a411d4700bcf87864dde',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'swBTC',
      brand: 'Swell',
      decimals: 8,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x8db2350d78abc13f5673a411d4700bcf87864dde',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
      yieldFetcher: swBtcFetcher,
      yieldKey: 'Swell Restaked BTC::SWBTC',
    },
    {
      // Lombard LBTC — non-rebasing, accruing exchange rate via
      // `getRate()` (1e18-scaled). Mint/redeem is native BTC off-chain
      // (no EVM deposit path); redemption is a ~9-day Babylon unbond.
      address: '0x8236a87084f8b84306f72007f36f2618a5634494',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'LBTC',
      brand: 'Lombard',
      decimals: 8,
      reader: 'swellGetRate',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'queued',
      yieldFetcher: lombardLbtcFetcher,
      yieldKey: 'LBTC',
    },
    {
      // ether.fi eBTC — Veda BoringVault. Rate via the Accountant's
      // `getRate()` (8-dec). Mint via the Teller; ~7-day queue exit.
      address: '0x657e8c867d8b37dcc18fa4caead9c45eb088c642',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'eBTC',
      brand: 'ether.fi',
      decimals: 8,
      reader: 'vedaAccountant',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x6ee3aaccf9f2321e49063c4f8da775ddbd407268',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
      readerExtras: {
        vedaAccountant: '0x1b293DC39F94157fA0D1D36d7e0090C8B8B8c13F',
      },
      yieldFetcher: ebtcFetcher,
      yieldKey: 'Ether fi Staked BTC::EBTC',
    },
    {
      // Bedrock uniBTC — flat 1:1 (no on-chain rate; verified). The
      // base/reserve token minted from wBTC and the required input for
      // brBTC. Yield is off-chain (points) — no APR feed today.
      address: '0x004e9c3ef86bc1ca1f0bb5c7662861ee93350568',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'uniBTC',
      brand: 'Bedrock',
      decimals: 8,
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x047d41f2544b7f63a8e991af2068a363d210d6da',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
    },
    {
      // Bedrock brBTC — flat 1:1 (no on-chain rate; verified). LRT
      // minted from uniBTC + wrapped BTC via the brBTC Vault
      // (0x1419…3386). Off-chain yield (no APR feed).
      address: '0x2ec37d45fcae65d9787ecf71dc85a444968f6646',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'brBTC',
      brand: 'Bedrock',
      decimals: 8,
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1419b48e5c1f5ce413cf02d6dcbe1314170e3386',
      mintInputAsset: '0x004e9c3ef86bc1ca1f0bb5c7662861ee93350568',
      withdrawalMode: 'queued',
    },
    {
      // PumpBTC pumpBTC — flat 1:1 (no on-chain rate; verified
      // 2026-06-03 — `asset()`/`getRate()`/`exchangeRate()`/
      // `convertToAssets()`/`pricePerShare()` all revert, exactly like
      // uniBTC/brBTC). Babylon BTC liquid staking; yield is off-chain
      // (points / BABY airdrops), no public APR feed → no yieldFetcher.
      // Minted 1:1 from WBTC via the WBTC `PumpStaking` proxy
      // `stake(amount)` (0x1fcca65f…, `pumpBTC()`==this token verified).
      // Separate per-collateral staking contracts exist
      // (FBTC 0x3d9bcca8…, cbBTC 0xc0c687a1…). Exit is a queue:
      // `unstakeRequest`→`claimSlot/claimAll` (~10-day T+10) or
      // `unstakeInstant` (currently 2% fee) — withdrawal reader deferred.
      address: '0xf469fbd2abcd6b9de8e169d128226c0fc90a012e',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'pumpBTC',
      brand: 'PumpBTC',
      decimals: 8,
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1fcca65fb6ae3b2758b9b2b394cb227eae404e1e',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
    },
    {
      // Solv SolvBTC — 1:1 BTC reserve token (18-dec, no direct yield).
      // The base/mint input for xSolvBTC. Mint/redeem via SolvBTCRouter
      // (poolId-keyed). No on-chain rate getter.
      address: '0x7a56e1c57c7475ccf742a1832b028f0456652f97',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'SolvBTC',
      brand: 'Solv',
      decimals: 18,
      underlyingDecimals: 8, // WBTC underlying is 8-dec; share is 18-dec
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1ff7d7c0a7d8e94046708c611dec5056a9d2b823',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
    },
    {
      // Solv xSolvBTC — Babylon-staked yield variant of SolvBTC
      // (18-dec). No on-chain rate getter; yield is off-chain. Mint by
      // staking SolvBTC via the SolvBTCRouter (poolId-keyed).
      address: '0xd9d920aa40f578ab794426f5c90f6c731d159def',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'xSolvBTC',
      brand: 'Solv',
      decimals: 18,
      underlyingDecimals: 8, // WBTC underlying is 8-dec; share is 18-dec
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1ff7d7c0a7d8e94046708c611dec5056a9d2b823',
      mintInputAsset: '0x7a56e1c57c7475ccf742a1832b028f0456652f97',
      withdrawalMode: 'queued',
    },
    {
      // Solv BTC+ — yield-aggregator BTC token (18-dec, no on-chain
      // rate). Mintable with WBTC / cbBTC / SolvBTC via the
      // SolvBTCRouter (poolId-keyed). APR from the Solv BTC+ stats API.
      address: '0xcea2daf93617b97504e05affc5bcf9b3922d3034',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      symbol: 'BTC+',
      brand: 'Solv',
      decimals: 18,
      underlyingDecimals: 8, // WBTC underlying is 8-dec; share is 18-dec
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1ff7d7c0a7d8e94046708c611dec5056a9d2b823',
      mintInputAsset: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599',
      withdrawalMode: 'queued',
      yieldFetcher: solvBtcPlusFetcher,
      yieldKey: 'SOLVBTCPLUS',
    },
    {
      // Lombard LBTCv — Veda BoringVault DeFi vault over LBTC ("Bitcoin Earn",
      // Lombard API wrapper `VAULT_WRAPPER_BTCE`; 8-dec). Share price via the
      // Veda Accountant `getRate()` (8-dec, ≈1.024 BTC/LBTCv) — the accountant
      // (`0x2863…3dCE`) is reached through the vault hook's `accountant()`. APY
      // from the Lombard vault API. Redeems to LBTC (~3-day vault-share queue);
      // priced via WBTC (≈BTC). Not mintable here (Veda Teller; no builder).
      address: '0x5401b8620e5fb570064ca9114fd1e135fd77d57c',
      underlying: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', // WBTC (BTC price ref)
      symbol: 'LBTCv',
      brand: 'Lombard',
      decimals: 8,
      reader: 'vedaAccountant',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'queued',
      readerExtras: {
        vedaAccountant: '0x28634d0c5edc67cf2450e74dea49b90a4ff93dce',
      },
      yieldFetcher: lombardLbtcvFetcher,
      yieldKey: 'LBTCv',
    },
  ],
  '146': [
    {
      address: '0xe5da20f15420ad15de0fa650600afc998bbe3955',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'stS',
      brand: 'Beets',
      decimals: 18,
      reader: 'beetsStS',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xe5da20f15420ad15de0fa650600afc998bbe3955',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      // 14-day Sonic validator unbond.
      withdrawalCooldownSeconds: 14 * 86_400,
      yieldFetcher: stsFetcher,
      yieldKey: 'Beets Staked Sonic::STS',
    },
  ],
  '43114': [
    {
      // Treehouse tAVAX — the tETH design on Avalanche, denominated in
      // BENQI sAVAX. Same Router / Vault / RedemptionController stack; see
      // the tETH entry on chain 1 for the three traps.
      //
      // The Avalanche Router carries NO `depositETH()` (selector-scanned
      // against the live runtime), so native AVAX is not an entry here —
      // the allowable set is sAVAX and WAVAX only.
      address: '0x14a84f1a61ccd7d1be596a6cc11fe33a36bc1646',
      underlying: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be', // sAVAX
      symbol: 'tAVAX',
      brand: 'Treehouse',
      decimals: 18,
      reader: 'treehouseTAsset',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x5f4d2e6c118b5e3c74f0b61de40f627ca9873d6e', // TreehouseRouter
      mintInputAsset: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
      withdrawalMode: 'fee-or-queued',
      // 17 d live — NOT the 7 d the docs describe. Read live by the reader.
      withdrawalCooldownSeconds: 17 * 86_400,
      yieldFetcher: tavaxFetcher,
      yieldKey: 'Treehouse AVAX::tAVAX',
      readerExtras: {
        treehouseFastlane: '0x3d00a639183b07e35efef044ee6cc14e8598a01c',
        treehouseRedemption: '0x765f6dc8496ca7ef1e4a391be10185229aacf04b',
      },
    },
    {
      address: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'sAVAX',
      brand: 'BENQI',
      decimals: 18,
      reader: 'benqiSavax',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      // 15-day cooldown + 2-day claim window.
      withdrawalCooldownSeconds: 15 * 86_400,
      yieldFetcher: savaxFetcher,
      yieldKey: 'SAVAX',
    },
    {
      // Ankr ankrAVAX (aAVAXc) — native AVAX stake via avalanchePool
      // `stakeAndClaimCerts()`; unstake via `claimCerts(amount)`
      // (queued). `ratio()` is shares-per-AVAX, so the reader inverts it.
      address: '0xc3344870d52688874b06d844e0c36cc39fc727f6',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ankrAVAX',
      brand: 'Ankr',
      decimals: 18,
      reader: 'ankrRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x7baa1e3bfe49db8361680785182b80bb420a836d',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: ankrFetcher,
      yieldKey: 'ANKRAVAX',
    },
  ],
  '999': [
    {
      // Treehouse tHYPE — the tETH design on HyperEVM, denominated in
      // Kinetiq kHYPE. Same stack; see the tETH entry on chain 1.
      //
      // No `depositETH()` on this Router either (selector-scanned), so the
      // entry set is kHYPE and WHYPE (0x5555…), never native HYPE.
      //
      // NO yield fetcher: DefiLlama carries no `treehouse-protocol` pool
      // for tHYPE and Treehouse's own `/apy` route serves tETH only, so the
      // row reports a `supplyRate` of 0 rather than an invented number.
      // The vault launched recently (share price 1.0007) — revisit with an
      // archival `convertToAssets` delta once it has real history.
      address: '0xb83d27f0f4133a7b0377a88089583131a903fdc7',
      underlying: '0xfd739d4e423301ce9385c1fb8850539d657c296d', // kHYPE
      symbol: 'tHYPE',
      brand: 'Treehouse',
      decimals: 18,
      reader: 'treehouseTAsset',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x1620b7e5c9099c8bcbc326b55e0b8440eda9d30a', // TreehouseRouter
      mintInputAsset: '0xfd739d4e423301ce9385c1fb8850539d657c296d',
      withdrawalMode: 'fee-or-queued',
      // 10 d live — again not the docs' 7 d. Read live by the reader.
      withdrawalCooldownSeconds: 10 * 86_400,
      readerExtras: {
        treehouseFastlane: '0x3018e0747549835faa8d06cf4809b1a2519fab31',
        treehouseRedemption: '0xcebbb011c2f29b0ed25895c54662bd1ccad30280',
      },
    },
    {
      // hbHYPE Ultra — Hyperbeat's ERC-4626 staked HYPE vault.
      // TokenizedAccount impl, 4626-compliant, accepts WHYPE.
      address: '0x96c6cbb6251ee1c257b2162ca0f39aa5fa44b1fb',
      // WHYPE — wrapped HYPE on HyperEVM (verify at boot if probing).
      underlying: '0x5555555555555555555555555555555555555555',
      symbol: 'hbHYPE',
      brand: 'Hyperbeat',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x96c6cbb6251ee1c257b2162ca0f39aa5fa44b1fb',
      mintInputAsset: '0x5555555555555555555555555555555555555555',
      withdrawalMode: 'queued',
      yieldFetcher: hbuhypeFetcher,
      yieldKey: 'Hyperbeat Ultra Hype::hbHYPE',
    },
    {
      // beHYPE (Hyperbeat × ether.fi). Rate getter
      // `StakingCore.BeHYPEToHYPE(1e18)` returns the 1e18-scaled
      // HYPE-per-beHYPE rate.
      address: '0xd8fc8f0b03eba61f64d08b0bef69d80916e5dda9',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'beHYPE',
      brand: 'Hyperbeat',
      decimals: 18,
      reader: 'hyperbeatBeHype',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xcead893b162d38e714d82d06a7fe0b0dc3c38e0b',
      mintInputAsset: 'native',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: hbhypeFetcher,
      yieldKey: 'hyperbeat x ether.fi HYPE::beHYPE',
      readerExtras: {
        hyperbeatStakingCore: '0xcead893b162d38e714d82d06a7fe0b0dc3c38e0b',
      },
    },
    {
      // kHYPE (Kinetiq). Rate via the separate StakingAccountant —
      // `kHYPEToHYPE(1e18)` returns 1e18-scaled HYPE-per-kHYPE.
      address: '0xfd739d4e423301ce9385c1fb8850539d657c296d',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'kHYPE',
      brand: 'Kinetiq',
      decimals: 18,
      reader: 'kinetiqKHype',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      withdrawalMode: 'fixed-cooldown',
      // On-chain `StakingManager.withdrawalDelay()` = 604800s (7 days).
      withdrawalCooldownSeconds: 7 * 86_400,
      // DefiLlama `kinetiq-khype` pool — hyperlend's nativeApy.kHYPE has
      // been persistently null (empty history), so DefiLlama is primary.
      yieldFetcher: kinetiqKHypeFetcher,
      yieldKey: 'Kinetiq Staked HYPE::kHYPE',
      readerExtras: {
        kinetiqAccountant: '0x9209648ec9d448ef57116b73a2f081835643dc7a',
      },
    },
    {
      // wstHYPE (Valantis). Rate via the underlying stHYPE
      // contract's `balancePerShare()` (wstHYPE wraps the same
      // share/balance state).
      address: '0x94e8396e0869c9f2200760af0621afd240e1cf38',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'wstHYPE',
      brand: 'Valantis',
      decimals: 18,
      reader: 'valantisWstHype',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xb96f07367e69e86d6e9c3f29215885104813eeae',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 86_400,
      // APR from Valantis's own analytics endpoint (the source behind
      // app.valantis.xyz/staking). hyperlend's `nativeApy` mirrors this
      // number but has gone null upstream — prefer the direct source.
      yieldFetcher: valantisStHypeFetcher,
      yieldKey: 'Staked HYPE Shares::WSTHYPE',
      readerExtras: {
        valantisStHype: '0xffaa4a3d97fe9107cef8a3f48c069f577ff76cc1',
      },
    },
  ],
  '137': [
    {
      // Stader MaticX on Polygon (bridge child token). The token
      // itself has no rate getter — `convertMaticXToMatic(1e18)` is
      // dispatched cross-chain to the Ethereum home contract via
      // `staderMaticXHomeChainId`/`staderMaticXHomeContract`. The
      // Polygon-side `totalSupply` reflects circulating MaticX on
      // Polygon (post-bridge); `totalAssets` is derived from that
      // supply at the canonical L1 rate.
      address: '0xfa68fb4628dff1028cfec22b4162fccd0d45efb6',
      // Polygon-side underlying = MATIC ERC-20 on Polygon.
      underlying: '0x0000000000000000000000000000000000001010',
      symbol: 'MaticX',
      brand: 'Stader',
      decimals: 18,
      reader: 'staderMaticX',
      isErc4626: false,
      isRebasing: false,
      // Polygon-side mint is not supported — users mint on Ethereum
      // and bridge. Flag `isMintable: false` so the calldata-sdk
      // routes deposits accordingly.
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'queued',
      yieldFetcher: maticxFetcher,
      yieldKey: 'MATICX',
      readerExtras: {
        staderMaticXHomeContract: '0xf03a7eb46d01d9ecaa104558c732cf82f6b6b645',
        staderMaticXHomeChainId: '1',
      },
    },
    {
      // Ankr ankrMATIC (aMATICc). On Polygon this is the bridged token;
      // mint/redeem runs through an `onlyIntegrator`-gated SwapPool, so
      // it is NOT permissionlessly mintable here — rate + yield only.
      // (POL staking proper happens on Ethereum.) `ratio()` is
      // shares-per-POL; the reader inverts it.
      address: '0x0e9b89007eee9c958c0eda24ef70723c2c93dd58',
      // Polygon-side underlying = POL/MATIC ERC-20 on Polygon.
      underlying: '0x0000000000000000000000000000000000001010',
      symbol: 'ankrMATIC',
      brand: 'Ankr',
      decimals: 18,
      reader: 'ankrRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'dex-only',
      yieldFetcher: ankrFetcher,
      yieldKey: 'ANKRMATIC',
    },
  ],
  '42220': [
    {
      // stCELO (StakedCelo). Value-accruing, non-rebasing. Rate via the
      // separate Manager — `toCelo(1e18)` returns the 1e18-scaled
      // CELO-per-stCELO rate. Withdrawals go through a 3-day LockedGold
      // unlock (request → per-group revoke → finishPendingWithdrawal).
      address: '0xc668583dcbdc9ae6fa3ce46462758188adfdfc24',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'stCELO',
      brand: 'StakedCelo',
      decimals: 18,
      reader: 'stceloManager',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x0239b96d10a434a56cc9e09383077a0490cf9398',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      withdrawalCooldownSeconds: 3 * 86_400,
      // No public APY API — APR is derived from the on-chain rate
      // growth (`Manager.toCelo`) over a 7-day window. Requires an
      // archival Celo RPC; falls back to 0 otherwise. See
      // `yields/intrinsic/fetchers/stcelo.ts`.
      yieldFetcher: stceloFetcher,
      yieldKey: 'Staked CELO::stCELO',
      readerExtras: {
        stceloManager: '0x0239b96d10a434a56cc9e09383077a0490cf9398',
      },
    },
  ],
  '56': [
    {
      // Lista slisBNB — BNB liquid staking. slisBNB is a plain ERC-20;
      // the 1e18-scaled BNB-per-slisBNB rate (`convertSnBnbToBnb`) and
      // total pooled BNB (`getTotalPooledBnb`) live on the
      // ListaStakeManager. Value-accruing, non-rebasing. Minted by
      // sending native BNB to `ListaStakeManager.deposit()`; exits run
      // through the StakeManager withdrawal queue (BNB validator
      // unbond, ~7–15 days).
      address: '0xb0b84d294e0c75a6abe60171b70edeb2efd14a1b',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'slisBNB',
      brand: 'Lista',
      decimals: 18,
      reader: 'listaSlisBnb',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x1adb950d8bb3da4be104211d5ab038628e477fe6',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: slisBnbFetcher,
      yieldKey: 'Lista Staked BNB::slisBNB',
      readerExtras: {
        listaStakeManager: '0x1adb950d8bb3da4be104211d5ab038628e477fe6',
      },
    },
    {
      // Binance wBETH on BNB — SAME token address as Ethereum, but a
      // different implementation behind the proxy (`WrapTokenV2BSC` vs
      // `WrapTokenV3ETH`), so the mint leg is NOT portable:
      //   Ethereum  `deposit(address referral)`          payable, native ETH
      //   BNB       `deposit(uint256 amount, address referral)` nonpayable,
      //             pulls the Binance-pegged ETH ERC-20 below → needs approve
      // The read surface and the pushed rate ARE identical (one oracle
      // 0x81720695… writes both chains, and `exchangeRate()` returns the
      // same value), hence the shared reader and the shared `yieldKey`.
      //
      // Exit is the same UnwrapTokenV1 queue at the same address, with the
      // same frozen-amount / 10-day-lock / operator-allocation caveats as
      // the Ethereum entry — see there.
      address: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      // Binance-pegged ETH on BSC — the deposit input, not native BNB.
      underlying: '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
      symbol: 'wBETH',
      brand: 'Binance',
      decimals: 18,
      reader: 'binanceWbeth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0xa2e3356610840701bdf5611a53974510ae27e2e1',
      mintInputAsset: '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
      withdrawalMode: 'queued',
      yieldFetcher: wbethFetcher,
      yieldKey: 'Wrapped Binance Beacon ETH::wBETH',
    },
    {
      // YieldNest ynBNB — ERC-4626 vault over slisBNB (Lista), restaked
      // via Kernel. Redeems to slisBNB synchronously; the BNB unstake
      // then runs through Lista's own queue, so from this vault's view
      // the exit is instant-to-slisBNB.
      address: '0x304b5845b9114182ecb4495be4c91a273b74b509',
      underlying: '0xb0b84d294e0c75a6abe60171b70edeb2efd14a1b',
      symbol: 'ynBNB',
      brand: 'YieldNest',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x304b5845b9114182ecb4495be4c91a273b74b509',
      mintInputAsset: '0xb0b84d294e0c75a6abe60171b70edeb2efd14a1b',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: yieldNestFetcher,
      yieldKey: 'YieldNest Restaked BNB::ynBNB',
    },
    {
      // YieldNest ynBNB MAX — ERC-4626 vault over wBNB (and BNB-LST
      // derivatives). First-come liquidity buffer; instant under normal
      // conditions, otherwise rebalanced from strategies.
      address: '0x32c830f5c34122c6afb8ae87aba541b7900a2c5f',
      underlying: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c',
      symbol: 'ynBNBx',
      brand: 'YieldNest',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x32c830f5c34122c6afb8ae87aba541b7900a2c5f',
      mintInputAsset: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c',
      withdrawalMode: 'instant-or-queued',
      yieldFetcher: yieldNestFetcher,
      yieldKey: 'ynBNB MAX::ynBNBx',
    },
    {
      // Ankr ankrBNB (aBNBc) — native BNB stake via BNBStakingPool
      // `stakeCerts()`; unstake via `unstakeCerts(shares)` (queued, ~7-15d
      // unbond). Unstake pulls ankrBNB via the operator at
      // 0xfe39…00C0e, so the withdraw builder approves that, not the pool.
      // `ratio()` is shares-per-BNB; the reader inverts it.
      address: '0x52f24a5e03aee338da5fd9df68d2b6fae1178827',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'ankrBNB',
      brand: 'Ankr',
      decimals: 18,
      reader: 'ankrRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x9e347af362059bf2e55839002c699f7a5bafe86e',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: ankrFetcher,
      yieldKey: 'ANKRBNB',
    },
  ],
  '8217': [
    {
      // Lair Finance stKAIA (Kaia) — the first liquid staking protocol
      // on Kaia (KIP-163 CnStakingV3 public delegation). Non-rebasing,
      // value-accruing. The stKAIA token IS the staking entry point
      // (payable proxy). Rate via `getRatio()` (1e18-scaled
      // KAIA-per-stKAIA, == `totalStaking()/totalSupply()`). Unstake is a
      // 7-day unbonding queue: `unstake(node, amount)` → `claim(id)`.
      address: '0x42952b873ed6f7f0a7e4992e2a9818e3a9001995',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'stKAIA',
      brand: 'Lair Finance',
      decimals: 18,
      reader: 'lairStKaia',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x42952b873ed6f7f0a7e4992e2a9818e3a9001995',
      mintInputAsset: 'native',
      withdrawalMode: 'fixed-cooldown',
      // 7-day unbonding per Lair docs / Kaia CnStakingV3 withdrawal delay.
      withdrawalCooldownSeconds: 7 * 86_400,
      yieldFetcher: stKaiaFetcher,
      yieldKey: 'STKAIA',
    },
  ],
  // Berachain (chainId 80094). Two rate-accruing LSTs (iBERA, beraETH)
  // plus four 1:1 BGT liquid-wrappers. BGT itself is non-transferable and
  // earned via Proof-of-Liquidity, so the wrappers are flat 1:1 and use
  // the generic `bgtWrapper1to1` reader (exchangeRate fixed at 1e18).
  '80094': [
    {
      // Infrared iBERA — BERA LST. ERC-4626-style accounting:
      // `convertToAssets(1e18)` ≈ 1.034 BERA/iBERA. Mint via
      // `mint(receiver)` payable; redeem is a `burn`→keeper→`claim` queue
      // (WithdraworLite at 0x8c0E1229…).
      address: '0x9b6761bf2397bb5a6624a856cc84a3a14dcd3fe5',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'iBERA',
      brand: 'Infrared',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x9b6761bf2397bb5a6624a856cc84a3a14dcd3fe5',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: iberaFetcher,
      yieldKey: 'iBERA',
    },
    {
      // Dinero beraETH — ETH LST. NOT ERC-4626; a wrapper over the
      // rberaETH base LST exposing `LSTPerToken()` (≈1.0535). Rate is
      // synced cross-chain from Dinero's mainnet apxETH engine. Mint via
      // the base LST rberaETH `deposit(...)`; redemption is a cross-chain
      // (LayerZero) queue.
      address: '0x6fc6545d5cde268d5c7f1e476d444f39c995120d',
      underlying: '0x3b0145f3cfa64bc66f5742f512f871665309075d',
      symbol: 'beraETH',
      brand: 'Dinero',
      decimals: 18,
      reader: 'dineroBeraEth',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      // Mint entry is the rberaETH base LST (`deposit(tokenIn,…,shouldWrap)`).
      // Only WETH is currently an authorized `tokenIn` (native ETH sentinel
      // exists in code but is not enabled on this deployment).
      mintContract: '0x3b0145f3cfa64bc66f5742f512f871665309075d',
      mintInputAsset: '0x2f6f07cdcf3588944bf4c42ac74ff24bf56e7590',
      withdrawalMode: 'queued',
      yieldFetcher: beraEthFetcher,
      yieldKey: 'beraETH',
    },
    {
      // Infrared iBGT — BGT wrapper, 1:1, non-accruing. No permissionless
      // mint (BGT is non-transferable; iBGT is minted by Infrared when you
      // stake PoL assets in an Infrared vault). Exit is DEX-only.
      address: '0xac03caba51e17c86c921e1f6cbfbdc91f8bb2e6b',
      underlying: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      // BGT is unpriced; the wrapper is 1:1 redeemable to BERA → price via WBERA.
      priceUnderlying: '0x6969696969696969696969696969696969696969',
      symbol: 'iBGT',
      brand: 'Infrared',
      decimals: 18,
      reader: 'bgtWrapper1to1',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'dex-only',
      // Best-effort: Infrared's ibgt apr-history route currently 404s, so
      // this stays undefined until they enable it.
      yieldFetcher: ibgtFetcher,
      yieldKey: 'iBGT',
    },
    {
      // BeraPaw LBGT — BGT wrapper, 1:1. Minted via BeraPawForge.mint
      // (claims your accrued BGT from a PoL reward vault). Redeem is a
      // queue that pays out native BERA (via BGT.redeem).
      address: '0xbaadcc2962417c01af99fb2b7c75706b9bd6babe',
      underlying: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      // BGT is unpriced; the wrapper is 1:1 redeemable to BERA → price via WBERA.
      priceUnderlying: '0x6969696969696969696969696969696969696969',
      symbol: 'LBGT',
      brand: 'BeraPaw',
      decimals: 18,
      reader: 'bgtWrapper1to1',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0xfeedb9750d6ac77d2e52e0c9eb8fb79f9de5cafe',
      mintInputAsset: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      withdrawalMode: 'queued',
      // PoL/BGT reward APY via DefiLlama (reward-driven, volatile).
      yieldFetcher: lbgtFetcher,
      yieldKey: 'LBGT',
    },
    {
      // Bearn yBGT — BGT wrapper, 1:1. Minted via `wrap(stakingToken)`
      // (claims accrued BGT). On-chain `redeem(amount)` exits instantly to
      // native BERA (minus fee).
      address: '0x7e768f47dfdd5dae874aac233f1bc5817137e453',
      underlying: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      // BGT is unpriced; the wrapper is 1:1 redeemable to BERA → price via WBERA.
      priceUnderlying: '0x6969696969696969696969696969696969696969',
      symbol: 'yBGT',
      brand: 'Bearn',
      decimals: 18,
      reader: 'bgtWrapper1to1',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x7e768f47dfdd5dae874aac233f1bc5817137e453',
      mintInputAsset: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      withdrawalMode: 'instant-or-queued',
    },
    {
      // Stride / BakerDAO stBGT — BGT wrapper, 1:1, non-accruing. No
      // permissionless mint (routed through an LP reward vault); on-chain
      // redeem path is unverified, so treated as DEX-only exit.
      address: '0x2cec7f1ac87f5345ced3d6c74bbb61bfae231ffb',
      underlying: '0x656b95e550c07a9ffe548bd4085c72418ceb1dba',
      // BGT is unpriced; the wrapper is 1:1 redeemable to BERA → price via WBERA.
      priceUnderlying: '0x6969696969696969696969696969696969696969',
      symbol: 'stBGT',
      brand: 'Stride',
      decimals: 18,
      reader: 'bgtWrapper1to1',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'dex-only',
      // No public APR source yet (Stride stake-stats reports 0); skipped.
    },
  ],
  '50': [
    {
      // PrimeStaking psXDC (Prime Numbers Labs) — XDC liquid staking.
      // The V3 vault is an ERC-4626-style share token; `asset()` is the
      // zero address (underlying is native XDC), so deposits go through
      // the payable `depositNative`/`stake` entries rather than the
      // standard 4626 `deposit` (which is disabled/`pure` here). Rate via
      // the generic 4626 reader (`convertToAssets(1e18)`); value-accruing,
      // non-rebasing. Exits are instant when the liquidity buffer covers
      // the request, otherwise a FIFO queue (`redeemWithQueue` →
      // `claimQueuedAssets`) with no fixed cooldown (network
      // `candidateWithdrawDelay` ~35 days is the worst-case upper bound).
      address: '0x98d916f5773ac0482b49856f2659d6c32114c4ba',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'psXDC',
      brand: 'PrimeStaking',
      decimals: 18,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x98d916f5773ac0482b49856f2659d6c32114c4ba',
      mintInputAsset: 'native',
      withdrawalMode: 'instant-or-queued',
      // Documented ~4.5% base APY — no live source yet (V3 NAV is flat at
      // 1.0 today, subgraph reward stream is noisy). See
      // `yields/intrinsic/fetchers/primestaking.ts`.
      yieldFetcher: psxdcFetcher,
      yieldKey: 'psXDC',
    },
  ],
  '1116': [
    {
      // Core DAO stCORE — official liquid staking. The rate getter lives
      // on the `Earn` contract (`stCORE.earn()`), not the token:
      // `getCurrentExchangeRate()` is 1e6-scaled CORE-per-stCORE. Mint via
      // `Earn.mint(validator)` (native CORE); exit via `redeem` → `withdraw`
      // (queued — Core round-based unbond).
      address: '0xb3a8f0f0da9ffc65318aa39e55079796093029ad',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'stCORE',
      brand: 'Core',
      decimals: 18,
      reader: 'coreEarnRate',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xf5fa1728babc3f8d2a617397fac2696c958c3409',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: coreLstFetcher,
      yieldKey: 'Liquid staked CORE::stCORE',
      readerExtras: {
        coreEarn: '0xf5fa1728babc3f8d2a617397fac2696c958c3409',
      },
    },
    {
      // b14g dualCORE — liquid dual-staking (CORE matched with staked BTC).
      // Value-accruing share token; backing CORE is `coreVault.totalStaked()`
      // (`dualCORE.coreVault()`) over the share supply. Deposit via
      // `coreVault.stake()` (native CORE); exit via `unbond(amount, instant)`
      // — 1-day unbond (0% fee) or instant (fee).
      address: '0xc5555ea27e63cd89f8b227dece2a3916800c0f4f',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'dualCORE',
      brand: 'b14g',
      decimals: 18,
      reader: 'coreStakedRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xee21ab613d30330823d35cf91a84ce964808b83f',
      mintInputAsset: 'native',
      withdrawalMode: 'fee-or-queued',
      yieldFetcher: coreLstFetcher,
      yieldKey: 'b14g dualCORE::DUALCORE',
      readerExtras: {
        coreStakeContract: '0xee21ab613d30330823d35cf91a84ce964808b83f',
      },
    },
    {
      // StakedCore SCORE — small liquid staking token that is its own
      // staking contract: `totalStaked()/totalSupply()` is the rate. Mint
      // via `deposit(validator)` (native CORE); exit via `withdraw(amount)`.
      address: '0xa20b3b97df3a02f9185175760300a06b4e0a2c05',
      underlying: '0x0000000000000000000000000000000000000000',
      symbol: 'SCORE',
      brand: 'StakedCore',
      decimals: 18,
      reader: 'coreStakedRatio',
      isErc4626: false,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0xa20b3b97df3a02f9185175760300a06b4e0a2c05',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
      yieldFetcher: coreLstFetcher,
      yieldKey: 'StakedCore::SCORE',
    },
    {
      // PumpBTC pumpBTC on Core — the BTC LST (8-dec), distinct from the
      // native-CORE LSTs above. Flat 1:1 (verified on-chain 2026-06-03:
      // `asset()`/`getRate()` revert), same as Ethereum pumpBTC. Here it
      // is a **bridged** token — there is no local `PumpStaking`
      // (canonical mint is on Ethereum/BNB; the on-chain minters are
      // small bridge proxies, not the staking interface, and supply is
      // negligible), so it is non-mintable on Core and exits via DEX.
      // `underlying` is SolvBTC.CORE (0x9410…f2aa) as the BTC price
      // reference (no plain WBTC exists on Core). Off-chain yield (points)
      // → no yieldFetcher.
      address: '0x5a2aa871954ebdf89b1547e75d032598356caad5',
      underlying: '0x9410e8052bc661041e5cb27fdf7d9e9e842af2aa',
      symbol: 'pumpBTC',
      brand: 'PumpBTC',
      decimals: 8,
      underlyingDecimals: 18, // SolvBTC.CORE underlying is 18-dec; share is 8-dec
      reader: 'offChain',
      isErc4626: false,
      isRebasing: false,
      isMintable: false,
      isNativeUnderlying: false,
      withdrawalMode: 'dex-only',
    },
  ],
  // Flare (14) — Firelight stXRP, the XRP cover-vault share token.
  '14': [
    {
      // Firelight stXRP — ERC-4626 vault share over FXRP (6-dec / 6-dec).
      // Non-rebasing: coverage premiums stream into the vault and compound
      // into `convertToAssets`; supply does not expand. Withdrawal is
      // period-based and delayed — `withdraw`/`redeem` create a
      // `WithdrawRequest` (shares burned immediately), `claimWithdraw` pays
      // out after the period ends (1–2 days with 1-day periods, 30–60 days
      // at feature-complete). No same-block redemption, so instant
      // `liquidity` is 0. Principal is slashable pro-rata after the
      // protocol's First-Loss Buffer.
      address: '0x4c18ff3c89632c3dd62e796c0afa5c07c4c1b2b3',
      underlying: '0xad552a648c74d49e10027ab8a618a3ad4901c5be', // FXRP
      symbol: 'stXRP',
      brand: 'Firelight',
      decimals: 6,
      underlyingDecimals: 6,
      reader: 'erc4626',
      isErc4626: true,
      isRebasing: false,
      isMintable: true,
      isNativeUnderlying: false,
      mintContract: '0x4c18ff3c89632c3dd62e796c0afa5c07c4c1b2b3',
      mintInputAsset: '0xad552a648c74d49e10027ab8a618a3ad4901c5be', // FXRP
      withdrawalMode: 'queued',
      // APR from the on-chain rate delta (no public APY API yet). Key is the
      // canonical token-list assetGroup — stXRP carries its own group.
      yieldFetcher: stxrpFetcher,
      yieldKey: 'Firelight stXRP::stXRP',
    },
    {
      // SparkDEX stFLR — Lido-style rebasing share token over native FLR.
      // Deposit via `submit()` (native FLR) or `submitWrapped()` (WFLR);
      // withdrawal via `requestWithdrawal()`/`claimWithdrawal()` (queued) or
      // `instantWithdrawal()` (fee). `brand` is the disambiguator against
      // Sceptre's sFLR — both wrap FLR.
      address: '0x0988c6ba244a90c07a917ebe609eb3264be716ff',
      underlying: '0x0000000000000000000000000000000000000000', // native FLR
      symbol: 'stFLR',
      brand: 'SparkDEX',
      decimals: 18,
      reader: 'flarePooledFlr',
      isErc4626: false,
      isRebasing: true,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x0988c6ba244a90c07a917ebe609eb3264be716ff',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
    },
    {
      // Sceptre sFLR — Lido-style rebasing share token over native FLR
      // (native Flare staking). Deposit via `deposit()` (native FLR);
      // withdrawal via unlock requests (`cooldownPeriod()`) or
      // `instantRedeem()` (fee). Distinct `brand` from SparkDEX stFLR.
      address: '0x12e605bc104e93b45e1ad99f9e555f659051c2bb',
      underlying: '0x0000000000000000000000000000000000000000', // native FLR
      symbol: 'sFLR',
      brand: 'Sceptre',
      decimals: 18,
      reader: 'flarePooledFlr',
      isErc4626: false,
      isRebasing: true,
      isMintable: true,
      isNativeUnderlying: true,
      mintContract: '0x12e605bc104e93b45e1ad99f9e555f659051c2bb',
      mintInputAsset: 'native',
      withdrawalMode: 'queued',
    },
  ],
}

/** Returns the registry entries for a chain, or `[]` when unsupported. */
export const getLstRegistry = (chainId: string): LstRegistryEntry[] =>
  LST_REGISTRY[chainId] ?? []
